import React, {
  ForwardRefRenderFunction,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";

import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";

import { Input } from "../../../../../../../components/Form";
import useTimezoneDate from "../../../../../../../hooks/useTimezoneDate";
import { DespatchTruck } from "../../../../../../../models/despatch";
import { DespatchesTruckFields } from "../../../../../../../util/enums";
import { useTruckMaintenance } from "../../hooks/useTruckMaintenance";
import { StyledModal, Section } from "./styles";

type Props = {
  onSubmit: (data: DespatchTruck) => void;
};

type Handles = {
  open: (data?: DespatchTruck) => void;
  close: () => void;
};

const TruckMaintenance: ForwardRefRenderFunction<Handles, Props> = (props, ref) => {
  const { onSubmit } = props;

  const { t: translate } = useTranslation();
  const { getDateValueUtcTimezone } = useTimezoneDate();

  const [isModalOpened, setIsModalOpened] = useState<boolean>(false);

  const {
    fields,
    onUpdateFieldEvent,
    onPopulateFields,
    onResetFields,
    fieldHasError,
    getErrorByField,
    validateFields,
  } = useTruckMaintenance();

  const onSubmitModal = () => {
    if (validateFields(fields)) {
      onSubmit(fields);
    }
  };

  const isUpdating = useMemo(() => fields?.id || fields?.uuid, [fields?.id, fields?.uuid]);

  const onOpenModal = useCallback((data?: DespatchTruck) => {
    if (data) onPopulateFields(data);
    setIsModalOpened(true);
  }, []);

  const onCloseModal = useCallback(() => {
    onResetFields();
    setIsModalOpened(false);
  }, []);

  useImperativeHandle(ref, () => ({
    open: onOpenModal,
    close: onCloseModal,
  }));

  const baseInputProps = (key: keyof DespatchTruck) => {
    const inputProps = {
      name: key,
      label: translate(key),
      value: fields?.[key] || "",
      onChange: onUpdateFieldEvent,
      error: fieldHasError(key),
      errorMessage: getErrorByField(key),
    };

    return inputProps;
  };

  return (
    <StyledModal
      open={isModalOpened}
      handleClose={onCloseModal}
      title={isUpdating ? translate("editTruck") : translate("newTruck")}
      showActionButtons
      dataCyTitle="despatch-truck-maintenance-title"
      actionButtonProps={{
        textSave: isUpdating ? translate("update") : translate("create"),
        dataCySave: "submitButton",
        onSave: onSubmitModal,
        onCancel: () => onCloseModal(),
      }}
      data-cy="despatch-truck-maintenance-modal"
    >
      <Section data-cy="despatch-truck-maintenance">
        <Grid container direction="row" spacing={2}>
          <Grid item xs={12} container direction="row" spacing={2}>
            <Grid item xs={3}>
              <Input
                {...baseInputProps(DespatchesTruckFields.FLEET_TYPE)}
                label={translate("type")}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <Input
                {...baseInputProps(DespatchesTruckFields.BOOKING_NUMBER)}
                label={translate("truckBookingNumber")}
              />
            </Grid>
            <Grid item xs={3}>
              <Input
                {...baseInputProps(DespatchesTruckFields.PALLETS_PER_FLEET)}
                label={translate("palletsPerTruck")}
                type="number"
                required
                min={0}
              />
            </Grid>
            <Grid item xs={3}>
              <Input
                {...baseInputProps(DespatchesTruckFields.DELIVERY_DATE)}
                required
                type="date"
                value={getDateValueUtcTimezone(
                  fields?.[DespatchesTruckFields.DELIVERY_DATE] as string
                )}
                onChange={(event) =>
                  onUpdateFieldEvent(event, {
                    value: new Date(event.target.value).toISOString(),
                    key: DespatchesTruckFields.DELIVERY_DATE,
                  })
                }
              />
            </Grid>
            <Grid item xs={3}>
              <Input
                {...baseInputProps(DespatchesTruckFields.DELIVERY_WINDOW_OPEN)}
                type="time"
                required
              />
            </Grid>
            <Grid item xs={3}>
              <Input
                {...baseInputProps(DespatchesTruckFields.DELIVERY_WINDOW_CLOSE)}
                type="time"
                required
              />
            </Grid>
          </Grid>
        </Grid>
      </Section>
    </StyledModal>
  );
};

export type { Handles as TruckMaintenanceHandles };
export default forwardRef(TruckMaintenance);
